/* REACT */
import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react'

/* 3RD PARTIES */
import { connect } from 'react-redux'
import {
  ChevronUp,
  Search,
  Lightbulb,
  MapPin,
  ChevronRight,
  BookOpenText,
  Building,
} from 'lucide-react'
import axios from 'axios'

/* COMPONENTS */

/* STORE */

/* STYLES */
import { QuickSearchViewWrapper } from '../style'
import {
  SearchBoxWrapper,
  SearchInput,
  SearchOuterWrapper,
  SearchResultsWrapper,
  SearchResultsContainer,
  SearchResultsItem,
  SearchResultsItemTitle,
  SearchResultsItemSubtitle,
  SearchUnderline,
  SearchResultCategoryContainer,
  SearchResultCategoryTitle,
  SearchInputWrapper,
  SearchIconWrapper,
  SearchResultsItemIcon,
  SearchResultCategoryIcon,
  SearchResultsItemTitleWrapper,
  SearchResultCategoryTitleWrapper,
  SearchResultsCategoryWrapper,
  SearchPlaceHolder,
} from './style'

/* UTILS/CONSTANTS/IMAGES-ASSETS */
import { DEFAULT_QUICK_SEARCH_SUGGESTIONS } from '../../../utils/constants'
import { getTranslations } from '../../../utils'

// SEPARATE AXIOS INSTANCE FOR INTENT FETCHING
const instance = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  timeout: 900000,
})

const CATEGORY_ICONS = {
  suggestions: <Lightbulb size={20} color={'#303030'} />,
  cities: <MapPin size={20} color={'#303030'} />,
  regions: <MapPin size={20} color={'#303030'} />,
  countries: <MapPin size={20} color={'#303030'} />,
  pages: <BookOpenText size={20} color={'#303030'} />,
  trips: <Building size={20} color={'#303030'} />,
}

const QuickSearch = ({
  organizationKey,
  organizationSlug,
  widgetMode,
  chatMode,
  language,
  isTestSearch,
  isInternalSearch,
}) => {
  const [searchQuery, setSearchQuery] = useState('')
  const [isInputHovered, setIsInputHovered] = useState(false)
  const [isFocused, setIsFocused] = useState(false)
  const [isSearching, setIsSearching] = useState(false)
  const [searchResults, setSearchResults] = useState([])
  const abortControllerRef = useRef(null)

  const setDefaultSearchResults = () => {
    if (DEFAULT_QUICK_SEARCH_SUGGESTIONS[organizationSlug]?.[language]) {
      setSearchResults(
        DEFAULT_QUICK_SEARCH_SUGGESTIONS[organizationSlug]?.[language]
      )
      return
    }
    setSearchResults([])
  }
  useEffect(() => {
    setDefaultSearchResults()
  }, [])

  const performSearch = useCallback(
    async query => {
      if (!query || query.trim() === '') {
        setSearchResults([])
        return
      }

      // Cancel any existing request.
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }

      // Create a new AbortController for this request
      const abortController = new AbortController()
      abortControllerRef.current = abortController

      setIsSearching(true)
      try {
        const response = await instance.post(
          '/search/quick',
          {
            searchTerm: query,
            isTestSearch,
            widgetMode: 'search',
            currentUrl: window.location.href,
          },
          {
            headers: {
              'Content-Type': 'application/json',
              'Organization-Key': organizationKey,
              'Organization-Slug': organizationSlug,
              'Organization-Mode': 'search',
              language: language,
            },
            signal: abortController.signal,
          }
        )

        // Only update state if this request wasn't cancelled
        if (!abortController.signal.aborted && response?.data?.data) {
          // SORT DATA BASED ON average_score ( if exists )
          // FINAL DATA IS ARRAY OF OBJECTS with keys title, type, average_score
          let sortedData = response?.data?.data.sort(
            (a, b) => b.average_score - a.average_score
          )

          // Within each sorted data, sort items list based on score ( if exists )
          sortedData = sortedData.map(item => ({
            ...item,
            items: item.items.sort((a, b) => b.score - a.score),
          }))

          setSearchResults(sortedData)
        }
      } catch (error) {
        // Ignore errors from cancelled requests
        if (axios.isCancel(error) || error.name === 'AbortError') {
          return
        }
        // TODO: Handle search error
      } finally {
        // Only update loading state if this request wasn't cancelled
        if (!abortController.signal.aborted) {
          setIsSearching(false)
        }
      }
    },
    [organizationKey, organizationSlug, isTestSearch, language]
  )

  // Cleanup: Cancel any pending requests on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
    }
  }, [])

  // Debounced search effect
  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      if (searchQuery.trim()) {
        performSearch(searchQuery)
      }
    }, 200) // 200ms debounce delay

    return () => {
      clearTimeout(debounceTimer)
    }
  }, [searchQuery, performSearch])

  const handleSearchQueryChange = value => {
    if (!value || value.trim() === '') {
      setDefaultSearchResults()
    }
    setSearchQuery(value)
  }

  const handleKeyPress = e => {
    if (e.key === 'Enter') {
      handleSearchQueryChange(e.target.value)
      // Perform immediate search on Enter
      if (e.target.value.trim()) {
        performSearch(e.target.value)
      }
    }
  }

  const resultsContainerWidth = useMemo(() => {
    const element = document.getElementById('maya-quick-search-box-wrapper')
    if (element) {
      return element.offsetWidth
    }
    return 0
  }, [isFocused])

  const bottomPositionOfSearchBoxWrapper = useMemo(() => {
    const element = document.getElementById('maya-quick-search-box-wrapper')
    if (element) {
      return element.offsetTop + element.offsetHeight
    }
    return 0
  }, [isFocused])

  const handleInputBlur = () => {
    // ADD A TIMEOUT TO PREVENT THE BLUR EVENT FROM BEING TRIGGERED IMMEDIATELY
    // IF TRIGGERED IMMEDIATELY, THEN THE SEARCH RESULTS REDIRECTION WILL NOT WORK
    setTimeout(() => {
      setIsFocused(false)
    }, 100)
  }

  const truncateText = (text, fontSize, fontWeight) => {
    // Add ... to text if it is too long
    if (!text) return text

    const element = document.getElementById('maya-quick-search-view-wrapper')
    if (!element) {
      return text
    }

    const elementWidth = element.offsetWidth

    // Account for padding and margins
    // SearchResultsItem has padding: 8px, padding-left: 10px
    // SearchResultsItemTitleWrapper has width: calc(100% - 30px)
    // So available width is approximately elementWidth - 30px (for icon) - 20px (for padding)
    const availableWidth = elementWidth - 50 // Approximate available width for subtitle

    // Get fontFamily from an existing element in the DOM
    // Try to find any h3 element first, or fall back to wrapper's font
    let fontFamily = 'sans-serif'
    const existingH3 = element.querySelector('h3')
    if (existingH3) {
      const computedStyle = window.getComputedStyle(existingH3)
      fontFamily = computedStyle.fontFamily || fontFamily
    } else {
      const computedStyle = window.getComputedStyle(element)
      fontFamily = computedStyle.fontFamily || fontFamily
    }

    // Use the passed fontSize and fontWeight parameters
    // They are already provided: '16px' for title, '12px' for subtitle

    // Create a temporary element to measure text width
    const tempElement = document.createElement('span')
    tempElement.style.visibility = 'hidden'
    tempElement.style.position = 'absolute'
    tempElement.style.whiteSpace = 'nowrap'
    tempElement.style.fontSize = fontSize
    tempElement.style.fontFamily = fontFamily
    tempElement.style.fontWeight = fontWeight
    document.body.appendChild(tempElement)

    // Measure the full text width
    tempElement.textContent = text
    const fullTextWidth = tempElement.offsetWidth

    // If text fits, return it
    if (fullTextWidth <= availableWidth) {
      document.body.removeChild(tempElement)
      return text
    }

    // Measure ellipsis width
    tempElement.textContent = '...'
    const ellipsisWidth = tempElement.offsetWidth

    // Calculate available width for text (subtract ellipsis width)
    const textAvailableWidth = availableWidth - ellipsisWidth

    // Binary search to find the maximum text length that fits
    let left = 0
    let right = text.length
    let maxLength = 0

    while (left <= right) {
      const mid = Math.floor((left + right) / 2)
      const testText = text.substring(0, mid)
      tempElement.textContent = testText
      const testWidth = tempElement.offsetWidth

      if (testWidth <= textAvailableWidth) {
        maxLength = mid
        left = mid + 1
      } else {
        right = mid - 1
      }
    }

    // Clean up
    document.body.removeChild(tempElement)

    // Reducing max length by 5 characters to ensure there's still some space on right side
    maxLength = maxLength - 5

    // Return truncated text with ellipsis
    if (maxLength < text.length) {
      return text.substring(0, maxLength) + '...'
    }

    return text
  }

  return (
    <QuickSearchViewWrapper id='maya-quick-search-view-wrapper'>
      <SearchOuterWrapper id='maya-quick-search-outer-wrapper'>
        <SearchBoxWrapper
          id='maya-quick-search-box-wrapper'
          $isSearchFocused={isFocused}
        >
          <SearchInputWrapper>
            <SearchInput
              id='maya-quick-search-input'
              type='text'
              placeholder={getTranslations('search_placeholder')}
              value={searchQuery}
              onKeyDown={handleKeyPress}
              onChange={e => handleSearchQueryChange(e.target.value)}
              onFocus={() => setIsFocused(true)}
              onBlur={handleInputBlur}
              onMouseEnter={() => setIsInputHovered(true)}
              onMouseLeave={() => setIsInputHovered(false)}
            ></SearchInput>
            <SearchIconWrapper>
              {!isFocused ? (
                <Search
                  size={20}
                  color={isInputHovered ? '#999999' : '#D9D9D9'}
                />
              ) : (
                <ChevronUp
                  size={20}
                  color={isInputHovered ? '#999999' : '#D9D9D9'}
                />
              )}
            </SearchIconWrapper>
          </SearchInputWrapper>
          {isFocused && <SearchUnderline />}
        </SearchBoxWrapper>
      </SearchOuterWrapper>
      {isFocused && (
        <SearchResultsWrapper
          role='dialog'
          aria-modal='true'
          aria-hidden={!isFocused}
          width={`${resultsContainerWidth}px`}
          $isSearchFocused={isFocused}
          $topPosition={bottomPositionOfSearchBoxWrapper}
        >
          <SearchResultsContainer>
            {isSearching ? (
              <SearchPlaceHolder>
                {getTranslations('quick_search_in_progress')}
              </SearchPlaceHolder>
            ) : !isSearching && searchResults.length === 0 ? (
              <SearchPlaceHolder>
                {getTranslations('no_search_results')}
              </SearchPlaceHolder>
            ) : (
              <SearchResultsCategoryWrapper>
                {searchResults.map((category, categoryIndex) => (
                  <React.Fragment key={categoryIndex}>
                    <SearchResultCategoryContainer>
                      {CATEGORY_ICONS[category.type] && (
                        <SearchResultCategoryIcon>
                          {CATEGORY_ICONS[category.type]}
                        </SearchResultCategoryIcon>
                      )}
                      <SearchResultCategoryTitleWrapper>
                        <SearchResultCategoryTitle>
                          {category.title} ({category.items.length})
                        </SearchResultCategoryTitle>
                      </SearchResultCategoryTitleWrapper>
                    </SearchResultCategoryContainer>
                    {category.items.map((item, itemIndex) => (
                      <SearchResultsItem
                        key={itemIndex}
                        href={item.url}
                        target='_blank'
                      >
                        <SearchResultsItemTitleWrapper>
                          <SearchResultsItemTitle
                            id={`maya-quick-search-item-title-${itemIndex}`}
                          >
                            {truncateText(item.title, '16px', '600')}
                          </SearchResultsItemTitle>
                          {item?.subtitle ? (
                            <SearchResultsItemSubtitle
                              id={`maya-quick-search-item-subtitle-${itemIndex}`}
                            >
                              {truncateText(item?.subtitle, '12px', '400')}
                            </SearchResultsItemSubtitle>
                          ) : null}
                        </SearchResultsItemTitleWrapper>
                        <SearchResultsItemIcon>
                          <ChevronRight size={20} color={'#303030'} />
                        </SearchResultsItemIcon>
                      </SearchResultsItem>
                    ))}
                  </React.Fragment>
                ))}
              </SearchResultsCategoryWrapper>
            )}
          </SearchResultsContainer>
        </SearchResultsWrapper>
      )}
    </QuickSearchViewWrapper>
  )
}

const mapStateToProps = state => ({
  isTestSearch: state.maya.isTestCase,
})

const mapDispatchToProps = dispatch => ({})

export default connect(mapStateToProps, mapDispatchToProps)(QuickSearch)
